import React, { Component } from 'react';
import { withRouter } from 'react-router';
import classNames from 'classnames';
import withFetch from '../../hoc/withFetch';
import withAuthProvider from '../../hoc/withAuthProvider';
import { REACT_APP_GEOJSON_LOCATION } from '../../config';
import { parseSelectedLayers } from './helpers/zipCodeParser';
import { getUnitsByFeatures } from './helpers/units';
import './create-district-map.scss';
import { createMapService } from '../../services/mapService';
import FilterPanel from './FilterPanel';
import MapContent from './MapContent';
import MapToolbar from './MapToolbar';
import Footer from './footer';
import { createUnitsService } from '../../services/unitsService';
import MapStateManager from './helpers/MapStateManager';
import DrawingManager from './helpers/DrawingManager';
import { UnitsVisibility } from './types/units-visibility';
import AreaManager from './helpers/AreaManager';
import { calculateDistrictData } from './helpers/DistrictDataCalculator';
import { loadDistrict } from './helpers/DistrictLoader';
import UnitsManager from './helpers/UnitsManager';
import SalesDistrictsManager from './helpers/SalesDistrictsManager';
import { toggleLayer } from './helpers/LayerManager';
import SaveDataManager from './helpers/SaveDataManager';
import { updateStateWithFilteredUnits, updateStateWithUnits, updateCalculationsOnly } from './helpers/FilterStateManager';
import { applyFilters as applyFiltersToUnits } from './helpers/FilterApplicator';
import { initialize as initializeMap } from './helpers/InitializationManager';
import { prepareDistrictDataForSave, navigateToDistrictAfterSave } from './helpers/DistrictSaveManager';
import { renderSelectionTypeFeatures } from './helpers/SelectionTypeManager';
import Report from './district-report/report';

class CreateDistrictMap extends Component {
    
    constructor(props) {
        super(props);
        this.state = {
            isLoading: true,
            isSaving: false,
            isSaved: false,
            isSalesDistricts: false,
            district: null,
            hasAreas: false,
            km2: '0',
            retailArea: '0',
            turnOver: '0',
            numberOfUnits: '0',
            selectedAreas: {
                aregion: [],
                county: [],
                localities: [],
                municipality: [],
                postalCodes: [],
                salesDistrictArea: 0,
                spatialGeoJson: {
                    type: "FeatureCollection",
                    features: []
                },
                units: []
            },
            loadingPercent: 0,
            units: [],
            allUnits: [],
            filteredUnits: null,
            filterModalIsOpen: false,
            unitsVisible: UnitsVisibility.ALL,
            demographicsOpen: false,
            undoIsActive: false,
            mapIsLoading: true,
            isLoadingMarkers: false
        };
        this.mapService = createMapService(props.fetch);
        this.unitsService = createUnitsService(props.fetch);
        this.mapState = new MapStateManager();
        this.areaManager = new AreaManager(this.mapState);
        this.drawingManager = new DrawingManager(this.mapState, this.areaManager);
        
        // Initialize class properties
        this.overlayLabels = null;
        
        // Sätt popup-instans i UnitsManager
        UnitsManager.setPopup(null);
        
        // Set up callback för att synkronisera selectedAreas-ändringar med debounce
        this.handleSelectedAreasChangedDebounced = null;
        this.createAreasForSaveDebounced = null;
        
        this.mapState.setOnSelectedAreasChanged((selectedAreas) => {
            // Debounce handleSelectedAreasChanged för att undvika onödiga beräkningar vid snabb val
            if (this.handleSelectedAreasChangedDebounced) {
                clearTimeout(this.handleSelectedAreasChangedDebounced);
            }
            this.handleSelectedAreasChangedDebounced = setTimeout(() => {
                this.handleSelectedAreasChanged(selectedAreas);
            }, 150); // Vänta 150ms innan beräkningar körs
        });
    }

    /**
     * Hanterar när selectedAreas ändras i MapStateManager
     * Uppdaterar beräkningar och React state
     */
    handleSelectedAreasChanged = (selectedAreas) => {
        // Beräkna data baserat på nya selected areas (inkluderar inkluderade units från filtret)
        const calculatedData = calculateDistrictData(
            selectedAreas, 
            this.state.units,
            this.state.district?.salesDistrictFilters || null, // Skicka med filters för att inkludera inkluderade units
            this.state.allUnits // Skicka med original units för att hitta inkluderade units
        );
        
        // Uppdatera React state med beräkningar
        this.setState({
            ...calculatedData
        });
        
        // Uppdatera markers om vi är i AREA-visibility mode
        UnitsManager.updateMarkersForSelectedAreas(
            this.state.unitsVisible,
            selectedAreas,
            this.state.units,
            this.mapState,
            this.props.fetch,
            this.state.district?.salesDistrictFilters || null, // Skicka med filters för att hantera inkluderade units
            this.state.allUnits // Skicka med original units för att hitta inkluderade units
        );
        
        // Uppdatera undo state
        this.handleUndoState();
    }

    // Update loading states in one batch
    updateLoadingStates = (isLoadingMarkers, isMapLoading) => {
        this.setState({
            isLoadingMarkers,
            mapIsLoading: isMapLoading
        });
    }

    handleMapStateChange = (newState) => {
        this.setState(newState);
    }

    async save({name, color, addWatcher}) {
        // Sätt isSaving för UI state
        this.setState({
            isSaving: true
        });

        // Anropa saveDistrict direkt med värdena
        const { match } = this.props;
        await this.saveDistrict(match.params.id, { name, color, addWatcher });
    }

    async saveDistrict(isEdit, { name, color, addWatcher }) {

        // Förbered district data
        const districtData = prepareDistrictDataForSave({
            selectedAreas: this.state.selectedAreas,
            district: this.state.district,
            selectedAreasFeatures: this.mapState.getSelectedAreas(),
            units: this.state.units,
            drawingManager: this.drawingManager,
            name,
            color,
            addWatcher,
            type: this.props.type
        });

        try {
            const result = await this.mapService.saveDistrict(districtData, isEdit);
            
            this.setState({
                isSaved: true,
                isSaving: false
            }, () => {
                navigateToDistrictAfterSave(
                    result,
                    this.props.type,
                    this.state.district.salesDistrictScenarioId
                );
            });
        } catch (error) {
            console.error('Failed to save district:', error);
            // Handle error appropriately
        }
    }

    async componentDidUpdate() {
        await this.handleNewDistrictCreation();
    }

    /**
     * Hantera skapande av nytt district om det behövs
     * Körs när vi är i "create"-läge (ingen id i URL) och inget district finns i state
     */
    async handleNewDistrictCreation() {
        const { match } = this.props;
        
        // Skapa nytt district om vi är i "create"-läge (ingen id i URL) och inget district finns
        if (!match.params.id && !this.state.district) {
            // Rensa currentDistrictId när vi skapar ett nytt district
            if (this.mapState.setCurrentDistrictId) {
                this.mapState.setCurrentDistrictId(null);
            }
            
            const district = await this.mapService.createDistrict(this.props.type, match.params.scenarioId);
            this.setState({
                isLoading: false,
                district
            });
        }
    }

    addTools(tools) {
        this.tools = tools;
    }

    renderHomeMarker(geoJSON) {
        if (geoJSON && geoJSON.length !== 0) {
            const markerCoordinates = geoJSON[0].geometry.coordinates;
            const center = new this.mapState.maps.LatLng(markerCoordinates[1], markerCoordinates[0]);
            this.drawingManager.renderHouseMarker(center);
        }
    }

    /**
     * Ladda och visa ett befintligt district
     * Använder DistrictLoader för att ladda data, sedan hanterar UI-relaterade saker
     * @param {string} id - District ID
     * @param {Array} units - Units att använda (från handleApiLoaded)
     */
    async fetchSalesDistrict(id, units) {
        // Sätt currentDistrictId och currentDistrictName i mapState så att StyleManager kan exkludera det från sales district styling
        if (this.mapState.setCurrentDistrictId) {
            // Name kommer att sättas när district laddas
            this.mapState.setCurrentDistrictId(id, null);
        }
        
        // Ladda district-data med DistrictLoader
        const district = await loadDistrict(
            id,
            this.mapService,
            this.mapState,
            (type) => this.fetchGeoJSON(type),
            (isLoadingMarkers, isMapLoading) => this.updateLoadingStates(isLoadingMarkers, isMapLoading)
        );
        
        // UI-relaterade operationer efter laddning
        this.renderHomeMarker(district.homeMarkerGeoJson);

        if (district.spatialGeoJson) {
            // Parsa layers för zoom-funktionalitet
            const parsedLayers = parseSelectedLayers(district.layerSelection);
            
            // Zoom till selected areas om district bara byggt med en type
            if (this.mapState.getDistrictTypes().length === 1) {
                const districtType = this.mapState.getDistrictTypes()[0];
                this.mapState.zoomToDistrict(parsedLayers[districtType]);
            } else {
                this.mapState.zoomToDistrict();
            }
        }

        // Skapa multipolygon för att spara
        this.createAreasForSave();

        // Skapa labels om de inte redan skapats
        if (!this.mapState.labelManager.overlayLabels) {
            this.mapState.labelManager.handleLabels({
                map: this.mapState.getMap(),
                maps: this.mapState.getMaps(),
                turfPolygons: this.mapState.getAllTurfPolygons(),
                selectionType: this.mapState.getSelectionType(),
                isSalesDistricts: this.state.isSalesDistricts,
                salesDistricts: SalesDistrictsManager.getSalesDistricts()
            });
        }
        
        // Beräkna data för selected areas
        // Använd units-parametern istället för this.state.units eftersom state kan vara ofullständigt
        const unitsForCalculation = units || this.state.units || this.state.allUnits;
        const calculatedData = calculateDistrictData(
            this.mapState.getSelectedAreas(), 
            unitsForCalculation,
            district.salesDistrictFilters || null, // Skicka med filters för att inkludera inkluderade units
            units || this.state.allUnits // Skicka med original units för att hitta inkluderade units
        );

        // Uppdatera state med district och beräknad data
        this.setState({
            district,
            ...calculatedData
        });
        
        // Sätt currentDistrictName i mapState när district är laddat
        if (this.mapState.setCurrentDistrictId && district.name) {
            this.mapState.setCurrentDistrictId(id, district.name);
        }

        // Applicera filters direkt om de finns, så att rätt units renderas från start
        // Filter-komponenterna kommer fortfarande att applicera filters när de mountas,
        // men detta säkerställer att rätt units visas omedelbart
        const unitsToUse = units || this.state.units || this.state.allUnits;
        let filteredUnits = unitsToUse;
        
        if (district.salesDistrictFilters) {
            filteredUnits = applyFiltersToUnits(
                unitsToUse,
                district.salesDistrictFilters,
                this.props.type
            );
            
            // Uppdatera state med filtrerade units så att Filter-komponenterna får rätt data
            this.setState({
                units: filteredUnits
            });
        }

        // Rendera units (filtrerade om filters finns)
        await UnitsManager.renderUnits(
            UnitsVisibility.ALL,
            filteredUnits,
            this.mapState.getSelectedAreas(),
            this.mapState,
            this.props.fetch,
            district.salesDistrictFilters || null, // Skicka med filters för att hantera inkluderade units
            units || this.state.allUnits // Skicka med original units för att hitta inkluderade units
        );
        this.setState({ unitsVisible: UnitsVisibility.ALL });
    }

    async handleApiLoaded(map, maps) {
        // Initialisera mapState först (behöver map och maps)
        await this.mapState.handleApiLoaded({ map, maps });
        
        // Använd InitializationManager för resten av initialiseringen
        await initializeMap({
            mapState: this.mapState,
            drawingManager: this.drawingManager,
            unitsService: this.unitsService,
            type: this.props.type,
            match: this.props.match,
            selectArea: (area, addToSelection = true) => this.selectArea(area, addToSelection),
            fetchSalesDistrict: (id, units) => this.fetchSalesDistrict(id, units),
            setState: (stateUpdate) => this.setState(stateUpdate),
            fetch: this.props.fetch
        });
    }

    async fetchGeoJSON(type) {
        const geoJSON = await this.mapService.fetchGeoJSON(`${REACT_APP_GEOJSON_LOCATION}/${type}.json`);        
        return geoJSON;
    }

    handleSelectionChange(e) {
        this.renderFeaturesByType(e.currentTarget.value);
    }

    hasSelectionType(type) {
        return this.mapState.getDistrictTypes().includes(type);
    }

    async renderFeaturesByType(type) {
        await renderSelectionTypeFeatures({
            type,
            mapState: this.mapState,
            fetchGeoJSON: (type) => this.fetchGeoJSON(type),
            updateLoadingStates: (isLoadingMarkers, isMapLoading) => 
                this.updateLoadingStates(isLoadingMarkers, isMapLoading),
            isSalesDistricts: this.state.isSalesDistricts
        });
    }

    createAreasForSave() {
        SaveDataManager.createAreasForSave(
            this.mapState.getSelectedAreas(),
            this.mapState.getAllTurfPolygons(),
            this.state.units,
            (saveData) => {
                // Callback när save-data är klar
                this.setState({
                    selectedAreas: saveData
                });
            },
            (percentage) => {
                // Callback för loading progress
                this.setState({
                    loadingPercent: percentage
                });
            },
            () => {
                // Callback för reset
                this.setState({
                    spatialGeoJson: null
                });
            }
        );
    }

    /**
     * Select or toggle an area
     * State-uppdatering sker automatiskt via handleSelectedAreasChanged callback
     */
    async selectArea(area, isToggle = true) {
        if (isToggle) {
            this.areaManager.toggleArea(area);
        } else {
            this.areaManager.selectArea(area);
        }
        
        // State-uppdatering sker automatiskt via callback i handleSelectedAreasChanged
        // Debounce createAreasForSave för att undvika onödiga Web Worker-anrop vid snabb val
        if (this.createAreasForSaveDebounced) {
            clearTimeout(this.createAreasForSaveDebounced);
        }
        this.createAreasForSaveDebounced = setTimeout(() => {
            this.createAreasForSave();
        }, 300); // Vänta 300ms innan Web Worker startas
    }

    handleUndoState() {        
        if (this.mapState.getHistory().length > 0) {
            this.setState({
                undoIsActive: true
            });
        } else {
            this.setState({
                undoIsActive: false
            });
        }
    }


    async selectSearchedArea(searchObj) {
        await this.renderFeaturesByType(searchObj.type);  
        const turfPolygons = this.mapState.getTurfPolygons(searchObj.type);
        const data = turfPolygons?.find(data => data.feature.properties.id === searchObj.id);
        this.selectArea(data.feature, false);
        await this.renderFeaturesByType('clear');
        this.mapState.zoomToDistrict([searchObj.id]);
        this.applyFiltersForSelectedAreas(this.state.units);
    }

    async applyFilters(units, filters) {
        await updateStateWithFilteredUnits(
            units,
            this.mapState,
            {
                district: {
                    ...this.state.district,
                    salesDistrictFilters: {
                        ...this.state.district.salesDistrictFilters,
                        ...filters
                    }
                }
            },
            (stateUpdate) => this.setState(stateUpdate),
            this.state.unitsVisible,
            this.props.fetch,
            this.state.allUnits // Skicka med original units för att hitta inkluderade units
        );

        return units;
    }

    setDataForSelected(units) {
        updateStateWithUnits(
            units,
            this.mapState,
            (stateUpdate) => this.setState(stateUpdate),
            this.state.district?.salesDistrictFilters || null, // Skicka med filters för att inkludera inkluderade units
            this.state.allUnits // Skicka med original units för att hitta inkluderade units
        );
    }

    applyFiltersForSelectedAreas(units) {
        updateCalculationsOnly(
            units,
            this.mapState,
            (stateUpdate) => this.setState(stateUpdate),
            this.state.district?.salesDistrictFilters || null, // Skicka med filters för att inkludera inkluderade units
            this.state.allUnits // Skicka med original units för att hitta inkluderade units
        );
    }

    async toggleSaleDistricts(e) {
        const { match } = this.props;
        const isVisible = e.currentTarget.checked;
        
        this.setState({
            isSalesDistricts: isVisible
        });

        // Hämta nuvarande district ID och name för matchning
        // TODO: När API:et stödjer id i spatialinfo, ändra till att bara matcha på id
        const currentDistrictId = match.params.id || null;
        const currentDistrictName = this.state.district?.name || null;

        await SalesDistrictsManager.toggle(
            isVisible,
            this.mapState,
            this.mapService,
            this.props.type,
            match.params.scenarioId,
            (isLoading) => this.setState({ mapIsLoading: isLoading }),
            currentDistrictId,
            currentDistrictName
        );
    }


    toggleFilterModal(open) {
        this.setState({
            filterModalIsOpen: open
        });
    }

    toggleDemographics(e) {
        this.setState({
            demographicsOpen: e.currentTarget.checked
        });
    }

    async toggleOverlayArea(event, type, color, isSelected) {
        const { match } = this.props;
        
        await toggleLayer(
            isSelected,
            type,
            color,
            this.mapState,
            this.mapService,
            (type) => this.fetchGeoJSON(type),
            this.props.type,
            match.params.scenarioId,
            (isLoading) => this.setState({ mapIsLoading: isLoading })
        );
    }

    undo() {
        const undoIsActive = this.mapState.undo();
        this.setState({ undoIsActive });
    }

    /**
     * Toggle units visibility
     * Använder UnitsManager för att hantera marker rendering
     */
    toggleUnits = async (unitsVisible) => {
        await UnitsManager.renderUnits(
            unitsVisible,
            this.state.units,
            this.mapState.getSelectedAreas(),
            this.mapState,
            this.props.fetch,
            this.state.district?.salesDistrictFilters || null, // Skicka med filters för att hantera inkluderade units
            this.state.allUnits // Skicka med original units för att hitta inkluderade units
        );

        this.setState({ 
            unitsVisible
        });
    }

    render() {
        return (
            <div className={classNames({
                'create-district-map': true,
                'create-district-map--is-loading-markers': this.state.isLoadingMarkers
            })}>
                <FilterPanel 
                    units={this.state.units}
                    allUnits={this.state.allUnits}  
                    totalUnitsInAreas={getUnitsByFeatures(this.mapState.getSelectedAreas(), this.state.allUnits).length}
                    numberOfUnits={this.state.numberOfUnits}
                    filters={this.state.district && this.state.district.salesDistrictFilters}
                    onApplyFilters={(units, filters) => this.applyFilters(units, filters)}
                    onSetDataForSelected={(units) => this.setDataForSelected(units)}
                    filterModalIsOpen={this.state.filterModalIsOpen}
                    onCloseModal={() => this.toggleFilterModal(false)}
                    type={this.props.type}
                    unitsVisible={this.state.unitsVisible}
                />

                <div className="create-district-map__map-container">
                    <MapContent 
                        isLoadingMarkers={this.state.isLoadingMarkers}
                        mapIsLoading={this.state.mapIsLoading}
                        onGoogleApiLoaded={(map, maps) => this.handleApiLoaded(map, maps)}
                        selectedAreas={this.state.selectedAreas}
                        demographicsOpen={this.state.demographicsOpen}
                    />

                    <MapToolbar 
                        // Drawing tools
                        drawPolygon={() => this.drawingManager.drawPolygon()}
                        drawCircle={() => this.drawingManager.drawCircle()}
                        stopDrawing={() => this.drawingManager.stopDrawing()}
                        toggleDrawHouse={(isToolActive) => this.drawingManager.toggleDrawHouse(isToolActive)}
                        undo={() => this.undo()}
                        undoIsActive={this.state.undoIsActive}
                        hasAreas={this.state.hasAreas}
                        isLoading={this.state.mapIsLoading}
                        
                        // Selection
                        selectionType={this.mapState.getSelectionType()}
                        hasSelectionType={(type) => this.hasSelectionType(type)}
                        onSelectionChange={(type) => this.handleSelectionChange(type)}
                        
                        // Filter and search
                        onToggleFilterModal={() => this.toggleFilterModal(!this.state.filterModalIsOpen)}
                        filterModalIsOpen={this.state.filterModalIsOpen}
                        allUnits={this.state.allUnits}
                        units={this.state.units}
                        selectSearchedArea={async (searchObj) => await this.selectSearchedArea(searchObj)}
                        numberOfUnits={this.state.numberOfUnits}
                        
                        // Map type
                        type={this.props.type}
                        
                        // Layer toggles
                        onToggleUnits={this.toggleUnits}
                        onToggleSaleDistricts={(e) => this.toggleSaleDistricts(e)}
                        unitsVisible={this.state.unitsVisible}
                        onToggleDemographics={(e) => this.toggleDemographics(e)}
                        onToggleOverlayArea={(event, type, color, isSelected) => this.toggleOverlayArea(event, type, color, isSelected)}
                        
                        // Tools
                        onAddTools={(tools) => this.mapState.addTools(tools)}
                    />
                </div>
{/*
                <Report 
                    type={this.props.type}
                    fetch={this.props.fetch}
                    match={this.props.match}
                />
*/}

                <Footer 
                    {...this.state.district} 
                    selectedUnits={this.state.selectedAreas?.units}
                    allUnits={this.state.allUnits}
                    area={this.state.area}
                    numberOfUnits={this.state.numberOfUnits}
                    turnOver={this.state.turnOver}
                    retailArea={this.state.retailArea}
                    isLoading={this.state.isLoading}
                    mapIsLoading={this.state.mapIsLoading}
                    isSaving={this.state.isSaving}
                    isSaved={this.state.isSaved}
                    isLongLoadingTime={this.state.isSaving && this.mapState.getSelectedAreas().length === 0}
                    save={(saveProps) => this.save(saveProps)}
                    loadingPercent={this.state.loadingPercent}
                    location={this.props}
                    type={this.props.type}
                    fetch={this.props.fetch} 
                    match={this.props.match}
                />
            </div>
        );
    }
};

export default withAuthProvider(withFetch(withRouter(CreateDistrictMap)));